import { DocumentSectionType } from '@alpod-ai-writer/shared';
import { cn,withRef } from '@udecode/cn';
import { IS_APPLE } from '@udecode/plate';
import { PlateElement, useFocused, useReadOnly,useSelected } from '@udecode/plate/react';
import { type TMentionElement } from '@udecode/plate-mention';
import React, { useCallback } from 'react';

import { useMounted } from '../../../hooks/useMounted.hook';
import { useCitationList } from '../../hooks/useCitationList.hook';
import { useDocumentEditor } from '../../hooks/useDocumentEditor.hook';
import { useSectionNav } from '../../hooks/useSectionNav.hook';

interface MentionElementProps {
    prefix?: string;
    onClick?: (mentionNode: any) => void;
}

export const MentionElement = withRef<typeof PlateElement, MentionElementProps>(({
    children,
    className,
    prefix,
    ...props
}, ref) => {
    const editor = useDocumentEditor();
    const { citationMap } = useCitationList();
    const { selectSectionByType } = useSectionNav(editor.template.config.sections);

    const element = props.element as TMentionElement;
    const selected = useSelected();
    const focused = useFocused();
    const mounted = useMounted();
    const readOnly = useReadOnly();

    const handleOnClick = useCallback(() => {
        if (element.value) {
            selectSectionByType(DocumentSectionType.CitationList, element.value);
        }
    }, [ selectSectionByType, element.value ]);

    const label = citationMap.get(element.value)?.userLabel ?? `(citace byla smazána @${element.value})`;

    return (
        <PlateElement
            as={'span'}
            ref={ref}
            className={cn(
                className,
                'inline-block rounded-md bg-gray-200 px-1.5 py-0.5 align-baseline text-sm font-medium',
                !readOnly && 'cursor-pointer',
                selected && focused && 'ring-2 ring-ring',
                element.children[0].bold === true && 'font-bold',
                element.children[0].italic === true && 'italic',
                element.children[0].underline === true && 'underline'
            )}
            onClick={handleOnClick}
            data-slate-value={element.value}
            contentEditable={false}
            draggable
            {...props}
        >
            {mounted && IS_APPLE ? (
                // Mac OS IME https://github.com/ianstormtaylor/slate/issues/3490
                <React.Fragment>
                    {children}
                    {prefix}
                    {label}
                </React.Fragment>
            ) : (
                // Others like Android https://github.com/ianstormtaylor/slate/pull/5360
                <React.Fragment>
                    {prefix}
                    {label}
                    {children}
                </React.Fragment>
            )}
        </PlateElement>
    );
});
